import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { 
  QrCode, History, Camera, CheckCircle, Loader2, X, MapPin, Image, Scan,
  Clock, LogIn, LogOut, ImageIcon
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import MobileLayout from '../components/layout/MobileLayout';
import QRScanner from '../components/QRScanner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';

// History Item Component
const HistoryItem = ({ item }) => {
  const getIcon = () => {
    switch (item.type) {
      case 'clock_in':
        return <LogIn className="w-5 h-5 text-green-600" />;
      case 'clock_out':
        return <LogOut className="w-5 h-5 text-orange-600" />;
      case 'scan':
        return <QrCode className="w-5 h-5 text-blue-600" />;
      case 'photo':
        return <Camera className="w-5 h-5 text-purple-600" />;
      default:
        return <CheckCircle className="w-5 h-5 text-slate-400" />;
    }
  };

  const getBadge = () => {
    switch (item.type) {
      case 'clock_in':
        return <Badge className="bg-green-100 text-green-700 text-[10px]">CLOCK IN</Badge>;
      case 'clock_out':
        return <Badge className="bg-orange-100 text-orange-700 text-[10px]">CLOCK OUT</Badge>;
      case 'scan':
        return <Badge className="bg-blue-100 text-blue-700 text-[10px]">QR SCAN</Badge>;
      case 'photo':
        return <Badge className="bg-purple-100 text-purple-700 text-[10px]">PHOTO</Badge>;
      default:
        return null;
    }
  };

  const getBackgroundColor = () => {
    switch (item.type) {
      case 'clock_in':
        return 'bg-green-50 border-l-4 border-l-green-500';
      case 'clock_out':
        return 'bg-orange-50 border-l-4 border-l-orange-500';
      case 'scan':
        return 'bg-blue-50 border-l-4 border-l-blue-500';
      case 'photo':
        return 'bg-purple-50 border-l-4 border-l-purple-500';
      default:
        return 'bg-white';
    }
  };

  // Generate Google Maps URL
  const getGoogleMapsUrl = () => {
    if (item.latitude && item.longitude) {
      return `https://www.google.com/maps?q=${item.latitude},${item.longitude}`;
    }
    return null;
  };

  const mapsUrl = getGoogleMapsUrl();

  return (
    <div className={`p-4 ${getBackgroundColor()}`} data-testid={`history-item-${item.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0">
          {getIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {getBadge()}
            <span className="text-xs text-slate-500">
              {format(new Date(item.timestamp), 'h:mm a')}
            </span>
          </div>
          <p className="font-medium text-slate-800">{item.title}</p>
          {item.subtitle && (
            <p className="text-sm text-slate-500">{item.subtitle}</p>
          )}
          {mapsUrl ? (
            <a 
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-white rounded-full border border-slate-200 text-xs font-medium text-blue-600 hover:bg-blue-50 hover:border-blue-300 transition-colors shadow-sm"
              data-testid={`maps-link-${item.id}`}
            >
              <MapPin className="w-3.5 h-3.5" />
              <span>View on Google Maps</span>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          ) : item.location ? (
            <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
              <MapPin className="w-3 h-3" />
              <span>Location recorded</span>
            </div>
          ) : null}
          {item.photo && (
            <img 
              src={item.photo} 
              alt="Patrol" 
              className="mt-2 w-full max-w-[200px] h-24 object-cover rounded-lg"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default function Patrol() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('scan');
  const [scans, setScans] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manualCode, setManualCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  
  // Photo upload state
  const [photoCaption, setPhotoCaption] = useState('');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [processingPhoto, setProcessingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const [scansData, photosData, attendanceData] = await Promise.all([
        api.getTodayScans(),
        api.getTodayPatrolPhotos(),
        api.getAttendanceStatus(),
      ]);
      setScans(scansData);
      setPhotos(photosData);
      setAttendance(attendanceData);
    } catch (error) {
      console.error('Error loading patrol data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Build combined history from all activities
  const buildHistory = useCallback(() => {
    const history = [];
    
    // Add clock in event
    if (attendance?.attendance?.clock_in) {
      history.push({
        id: 'clock_in',
        type: 'clock_in',
        title: 'Clocked In',
        subtitle: attendance.attendance.site_name || 'Started shift',
        timestamp: attendance.attendance.clock_in,
        location: attendance.attendance.clock_in_latitude && attendance.attendance.clock_in_longitude,
        latitude: attendance.attendance.clock_in_latitude,
        longitude: attendance.attendance.clock_in_longitude,
      });
    }
    
    // Add clock out event
    if (attendance?.attendance?.clock_out) {
      history.push({
        id: 'clock_out',
        type: 'clock_out',
        title: 'Clocked Out',
        subtitle: attendance.attendance.site_name || 'Ended shift',
        timestamp: attendance.attendance.clock_out,
        location: attendance.attendance.clock_out_latitude && attendance.attendance.clock_out_longitude,
        latitude: attendance.attendance.clock_out_latitude,
        longitude: attendance.attendance.clock_out_longitude,
      });
    }
    
    // Add QR scans
    scans.forEach((scan) => {
      history.push({
        id: `scan_${scan.id}`,
        type: 'scan',
        title: scan.checkpoint_name || 'Checkpoint Scanned',
        subtitle: scan.site_name,
        timestamp: scan.scanned_at,
        location: scan.latitude && scan.longitude,
        latitude: scan.latitude,
        longitude: scan.longitude,
      });
    });
    
    // Add patrol photos
    photos.forEach((photo) => {
      history.push({
        id: `photo_${photo.id}`,
        type: 'photo',
        title: photo.caption || 'Patrol Photo',
        subtitle: photo.site_name,
        timestamp: photo.timestamp,
        location: photo.latitude && photo.longitude,
        latitude: photo.latitude,
        longitude: photo.longitude,
        photo: photo.photo,
      });
    });
    
    // Sort by timestamp (most recent first)
    history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    return history;
  }, [attendance, scans, photos]);

  const history = buildHistory();

  const getLocation = async () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({});
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
        () => resolve({}),
        { timeout: 5000, enableHighAccuracy: true }
      );
    });
  };

  const handleScanComplete = async (scannedCode) => {
    setShowScanner(false);
    setSubmitting(true);
    
    try {
      const location = await getLocation();
      await api.createScan({
        checkpoint_id: scannedCode.trim(),
        ...location,
      });
      
      toast({
        title: "Checkpoint Scanned!",
        description: "Your patrol checkpoint has been recorded.",
      });
      
      loadData();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Scan Failed",
        description: error.message || "Failed to record scan",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!manualCode.trim()) return;
    
    setSubmitting(true);
    try {
      const location = await getLocation();
      await api.createScan({
        checkpoint_id: manualCode.trim(),
        ...location,
      });
      
      toast({
        title: "Checkpoint Recorded!",
        description: "Manual checkpoint entry has been saved.",
      });
      
      setManualCode('');
      loadData();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Entry Failed",
        description: error.message || "Failed to record entry",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Compress image and add timestamp overlay
  const compressImage = (base64, maxWidth = 1000, quality = 0.6) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          let width = img.width || 800;
          let height = img.height || 600;
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Add timestamp
          const timestamp = new Date().toLocaleString();
          const fontSize = Math.max(12, Math.floor(width / 40));
          ctx.font = `bold ${fontSize}px Arial`;
          ctx.fillStyle = 'rgba(0,0,0,0.7)';
          const textWidth = ctx.measureText(timestamp).width;
          ctx.fillRect(width - textWidth - 20, height - fontSize - 16, textWidth + 16, fontSize + 12);
          ctx.fillStyle = '#FFFFFF';
          ctx.fillText(timestamp, width - textWidth - 12, height - 10);
          
          resolve(canvas.toDataURL('image/jpeg', quality));
        } catch (e) {
          resolve(base64); // Return original if processing fails
        }
      };
      
      img.onerror = () => resolve(base64); // Return original if load fails
      img.src = base64;
    });
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setProcessingPhoto(true);
    
    const maxSize = 600; // max width/height — keeps base64 under 200KB
    const quality = 0.4; // aggressive JPEG compression for patrol evidence
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new window.Image();
      img.onload = () => {
        try {
          let { width, height } = img;
          
          // Scale down
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            } else {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }
          
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const compressed = canvas.toDataURL('image/jpeg', quality);
          console.log('[Patrol] Compressed photo:', Math.round(compressed.length / 1024), 'KB');
          setSelectedPhoto(compressed);
        } catch (err) {
          console.error('[Patrol] Compression error:', err);
          // Even on error, try a simpler compression
          try {
            const canvas = document.createElement('canvas');
            canvas.width = maxSize;
            canvas.height = maxSize;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, maxSize, maxSize);
            setSelectedPhoto(canvas.toDataURL('image/jpeg', 0.3));
          } catch {
            toast({ variant: "destructive", title: "Error", description: "Photo too large to process" });
          }
        }
        setProcessingPhoto(false);
      };
      img.onerror = () => {
        // Image failed to load — don't send uncompressed raw data
        setProcessingPhoto(false);
        toast({ variant: "destructive", title: "Error", description: "Could not process this photo. Try taking another." });
      };
      img.src = event.target?.result;
    };
    reader.onerror = () => {
      setProcessingPhoto(false);
      toast({ variant: "destructive", title: "Error", description: "Could not read photo" });
    };
    reader.readAsDataURL(file);
  };

  const handlePhotoUpload = async () => {
    if (!selectedPhoto) return;
    
    setUploadingPhoto(true);
    try {
      console.log('[Patrol] Starting photo upload...');
      console.log('[Patrol] Photo size:', Math.round(selectedPhoto.length / 1024), 'KB');
      
      const location = await getLocation();
      console.log('[Patrol] Location:', location);
      
      const result = await api.createPatrolPhoto({
        photo: selectedPhoto,
        caption: photoCaption,
        ...location,
      });
      
      console.log('[Patrol] Upload success:', result);
      
      toast({
        title: "Photo Uploaded!",
        description: "Your patrol photo has been saved.",
      });
      
      setSelectedPhoto(null);
      setPhotoCaption('');
      loadData();
    } catch (error) {
      console.error('[Patrol] Upload error:', error);
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description: error.message || "Failed to upload photo. Please try again.",
      });
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (loading) {
    return (
      <MobileLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      <div className="p-4" data-testid="patrol-page">
        <h1 className="text-2xl font-bold text-slate-800 mb-4">Patrol</h1>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="scan" className="flex items-center gap-2">
              <Scan className="w-4 h-4" />
              <span className="hidden sm:inline">Scan</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              <span className="hidden sm:inline">History</span>
              {history.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">
                  {history.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="photos" className="flex items-center gap-2">
              <Camera className="w-4 h-4" />
              <span className="hidden sm:inline">Photos</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="scan" className="mt-4 space-y-4">
            {/* QR Scanner Card */}
            <Card>
              <CardContent className="p-4">
                <Button
                  onClick={() => setShowScanner(true)}
                  className="w-full h-32 bg-blue-700 hover:bg-blue-800 flex flex-col items-center justify-center gap-3"
                  disabled={submitting}
                  data-testid="open-qr-scanner-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-10 h-10 animate-spin" />
                  ) : (
                    <>
                      <Scan className="w-10 h-10" />
                      <span className="text-lg font-semibold">Open QR Scanner</span>
                    </>
                  )}
                </Button>
                <p className="text-center text-slate-500 text-sm mt-3">
                  Tap to scan a checkpoint QR code with your camera
                </p>
              </CardContent>
            </Card>

            {/* Today's Stats */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Scans Today</p>
                    <p className="text-3xl font-bold text-blue-700">{scans.length}</p>
                  </div>
                  <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center">
                    <QrCode className="w-7 h-7 text-blue-700" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            {/* History Summary */}
            <Card className="mb-4">
              <CardContent className="p-4">
                <h3 className="font-semibold text-slate-800 mb-3">Today's Activity Summary</h3>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <LogIn className="w-5 h-5 text-green-600 mx-auto mb-1" />
                    <p className="text-xs text-slate-600">Clock In</p>
                    <p className="font-bold text-green-700">
                      {attendance?.attendance?.clock_in ? '1' : '0'}
                    </p>
                  </div>
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <QrCode className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                    <p className="text-xs text-slate-600">Scans</p>
                    <p className="font-bold text-blue-700">{scans.length}</p>
                  </div>
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <Camera className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                    <p className="text-xs text-slate-600">Photos</p>
                    <p className="font-bold text-purple-700">{photos.length}</p>
                  </div>
                  <div className="p-2 bg-orange-50 rounded-lg">
                    <LogOut className="w-5 h-5 text-orange-600 mx-auto mb-1" />
                    <p className="text-xs text-slate-600">Clock Out</p>
                    <p className="font-bold text-orange-700">
                      {attendance?.attendance?.clock_out ? '1' : '0'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Activity Timeline */}
            <Card>
              <CardContent className="p-0 divide-y divide-slate-100">
                {history.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    <History className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p className="font-medium">No activity today</p>
                    <p className="text-sm mt-1">Clock in to start your shift</p>
                  </div>
                ) : (
                  history.map((item) => (
                    <HistoryItem key={item.id} item={item} />
                  ))
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="photos" className="mt-4 space-y-4">
            {/* Upload Photo */}
            <Card>
              <CardContent className="p-4 space-y-3">
                <h3 className="font-semibold text-slate-800">Upload Patrol Photo</h3>
                
                {selectedPhoto ? (
                  <div className="relative">
                    <img 
                      src={selectedPhoto} 
                      alt="Selected" 
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => setSelectedPhoto(null)}
                      className="absolute top-2 right-2 bg-white rounded-full p-1 shadow"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : processingPhoto ? (
                  <div className="w-full h-32 border-2 border-dashed border-blue-300 rounded-lg flex flex-col items-center justify-center gap-2 bg-blue-50">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    <span className="text-sm text-blue-600">Adding timestamp...</span>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-32 border-2 border-dashed border-blue-500 rounded-lg flex flex-col items-center justify-center gap-2 hover:bg-blue-50 transition-colors bg-blue-50/50"
                  >
                    <Camera className="w-10 h-10 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">Take Photo</span>
                  </button>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
                
                <Textarea
                  placeholder="Add a caption (optional)"
                  value={photoCaption}
                  onChange={(e) => setPhotoCaption(e.target.value)}
                  rows={2}
                />
                
                <Button
                  onClick={handlePhotoUpload}
                  disabled={uploadingPhoto || processingPhoto || !selectedPhoto}
                  className="w-full bg-blue-700 hover:bg-blue-800"
                >
                  {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                </Button>
              </CardContent>
            </Card>

            {/* Photo Gallery */}
            <div className="grid grid-cols-2 gap-3">
              {photos.length === 0 ? (
                <div className="col-span-2 p-8 text-center text-slate-500 bg-white rounded-lg border">
                  <Image className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No photos today</p>
                </div>
              ) : (
                photos.map((photo) => (
                  <Card key={photo.id} className="overflow-hidden">
                    <img 
                      src={photo.photo} 
                      alt={photo.caption || 'Patrol photo'}
                      className="w-full h-32 object-cover"
                    />
                    <CardContent className="p-2">
                      <p className="text-xs text-slate-500 truncate">
                        {format(new Date(photo.timestamp), 'h:mm a')}
                      </p>
                      {photo.caption && (
                        <p className="text-xs text-slate-700 truncate">{photo.caption}</p>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* QR Scanner Modal */}
      <QRScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleScanComplete}
      />
    </MobileLayout>
  );
}
